import type { MantineTheme } from '@mantine/core';
import type {
	BarChartConfig,
	BiMetricConfig,
	LineChartConfig,
	NumberChartConfig,
	PieChartConfig,
	ScatterChartConfig,
	TableChartConfig,
	XAxisConfig,
} from '@repo/api-codegen';
import { getTextHeight, toTitleCase } from '../utils';

// Helper class for the chart config that provide convenience methods to
export class ChartConfigHelper {
	lineHeight: number;
	fontSize: number;
	fontWeight: string;

	constructor(
		private readonly chartConfig: BiMetricConfig['chart_config'],
		private readonly mantineTheme: MantineTheme
	) {
		this.fontSize = 11;
		this.fontWeight = '450';
		this.lineHeight = getTextHeight(
			'a',
			this.mantineTheme.fontFamily || '',
			this.fontSize
		);
	}

	get isLineChart(): boolean {
		return this.chartConfig.chartType === 'line';
	}
	get isBarChart(): boolean {
		return this.chartConfig.chartType === 'bar';
	}
	get isPieChart(): boolean {
		return this.chartConfig.chartType === 'pie';
	}
	get isScatterChart(): boolean {
		return this.chartConfig.chartType === 'scatter';
	}
	get isTableChart(): boolean {
		return this.chartConfig.chartType === 'table';
	}
	get isNumberChart(): boolean {
		return this.chartConfig.chartType === 'number';
	}

	getAsBarChartConfig(): BarChartConfig {
		return this.chartConfig as BarChartConfig;
	}

	getAsLineChartConfig(): LineChartConfig {
		return this.chartConfig as LineChartConfig;
	}

	getAsPieChartConfig(): PieChartConfig {
		return this.chartConfig as PieChartConfig;
	}

	getAsScatterChartConfig(): ScatterChartConfig {
		return this.chartConfig as ScatterChartConfig;
	}

	getAsTableChartConfig(): TableChartConfig {
		return this.chartConfig as TableChartConfig;
	}

	getAsNumberChartConfig(): NumberChartConfig {
		return this.chartConfig as NumberChartConfig;
	}

	get colors(): string[] {
		return [
			this.mantineTheme.other.getColor('icon/chart/1'),
			this.mantineTheme.other.getColor('icon/chart/2'),
			this.mantineTheme.other.getColor('icon/chart/3'),
			this.mantineTheme.other.getColor('icon/chart/4'),
			this.mantineTheme.other.getColor('icon/chart/5'),
			this.mantineTheme.other.getColor('icon/chart/6'),
			this.mantineTheme.other.getColor('icon/chart/7'),
			this.mantineTheme.other.getColor('icon/chart/8'),
			this.mantineTheme.other.getColor('icon/chart/9'),
			this.mantineTheme.other.getColor('icon/chart/10'),
		];
	}

	get xIsTime(): boolean {
		if ('axes' in this.chartConfig) {
			return (
				this.chartConfig.columnConfigs?.[this.chartConfig.axes.x[0]]
					?.columnType === 'date'
			);
		}
		return false;
	}

	get forcexAxisRotation(): boolean {
		return typeof this.chartConfig.xAxisConfig?.xAxisTickRotation === 'number';
	}

	get xAxisTimeInterval(): XAxisConfig['xAxisTimeInterval'] {
		return this.chartConfig.xAxisConfig?.xAxisTimeInterval || 'auto';
	}

	get xAxisTickRotation(): number {
		return this.chartConfig.xAxisConfig?.xAxisTickRotation ?? 0;
	}

	get yAxisStartAtZero(): boolean {
		return this.chartConfig.yAxisConfig?.yAxisStartAtZero ?? true;
	}

	get showGrid(): boolean {
		return this.chartConfig.showGrid ?? true;
	}

	get showLegend(): boolean {
		return this.chartConfig.showLegend ?? true;
	}

	get lineType(): string {
		return this.getAsLineChartConfig().lineType ?? 'normal';
	}

	get shouldShowXAxisTitle(): boolean {
		return this.chartConfig.xAxisConfig?.xAxisShowTitle ?? false;
	}

	get xAxisTitle(): string {
		if (
			this.chartConfig.chartType === 'bar' ||
			this.chartConfig.chartType === 'line' ||
			this.chartConfig.chartType === 'scatter'
		) {
			return (
				this.chartConfig.xAxisConfig?.xAxisCustomTitle ??
				toTitleCase(this.chartConfig.axes.x[0] || '')
			);
		}
		return 'X-axis';
	}

	get yAxisTitle(): string {
		if (
			this.chartConfig.chartType === 'bar' ||
			this.chartConfig.chartType === 'line' ||
			this.chartConfig.chartType === 'scatter'
		) {
			return (
				this.chartConfig.yAxisConfig?.yAxisCustomTitle ??
				toTitleCase(this.chartConfig.axes.y[0] || '')
			);
		}
		return 'Y-axis';
	}

	get shouldShowYAxisTitle(): boolean {
		return this.chartConfig.yAxisConfig?.yAxisShowTitle ?? false;
	}

	get shouldShowXAxisTicks(): boolean {
		return this.chartConfig.xAxisConfig?.xAxisShowTicks ?? true;
	}

	get shouldShowYAxisTicks(): boolean {
		return this.chartConfig.yAxisConfig?.yAxisShowTicks ?? true;
	}

	get xAxisLabelRotationValue(): 'auto' | '0' | '45' | '90' {
		const value = this.chartConfig.xAxisConfig?.xAxisTickRotation ?? 'auto';
		return String(value) as 'auto' | '0' | '45' | '90';
	}
}
